import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from '@reduxjs/toolkit';
import dayjs from 'dayjs';

import { dashboardAPI as API } from '../../../axiosConfig';

const URL = 'payment-options-proxy?endpoint=paymentdashboard/maintenance/';

const SLICE_NAME = 'paymentOptionsMaintenanceRules';
const LIST_MAINTENANCE_RULE = `${SLICE_NAME}/maintenance-rules/list`;
const DELETE_MAINTENANCE_RULE = `${SLICE_NAME}/maintenance-rules/delete`;
const CREATE_MAINTENANCE_RULE = `${SLICE_NAME}/maintenance-rules/create`;

export const getMaintenanceRuleList = createAsyncThunk(
  LIST_MAINTENANCE_RULE,
  async (params, { rejectWithValue }) => {
    const serializedParams = Object.entries(params || {})
      .filter(([, value]) => value !== undefined && value !== null)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
      )
      .join('AND');

    const url = serializedParams
      ? `${URL}getRules?${serializedParams}`
      : `${URL}getRules`;

    try {
      const { data } = await API.get(url);

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const deleteMaintenanceRule = createAsyncThunk(
  DELETE_MAINTENANCE_RULE,
  async (data) => {
    const url = `${URL}deleteRules`;

    const response = await API.delete(url, {
      data,
    }).catch((error) => {
      console.error(JSON.stringify(error));
      return error && error.response;
    });

    return response.data;
  },
);

export const createMaintenanceRule = createAsyncThunk(
  CREATE_MAINTENANCE_RULE,
  async (payload, { rejectWithValue }) => {
    try {
      const url = `${URL}addRule`;
      const { data, status } = await API.post(url, payload);

      if (status !== 202) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const initialState = {
  maintenanceRuleList: [],
  maintenanceRuleListStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed',
  totalElements: 0,

  filteredStatus: [],
  filteredLoB: [],
  filteredPlatform: [],
  filteredCurrency: [],
  filteredMethodCode: [],
  filteredStartDate: null,
  filteredEndDate: null,
};

const paymentOptionsMaintenanceRulesSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    resetFilters(state) {
      state.filteredStatus = [];
      state.filteredLoB = [];
      state.filteredPlatform = [];
      state.filteredCurrency = [];
      state.filteredMethodCode = [];
      state.filteredStartDate = null;
      state.filteredEndDate = null;
    },
    setFilterStatus(state, { payload }) {
      state.filteredStatus = payload;
    },
    setFilterLoB(state, { payload }) {
      state.filteredLoB = payload;
    },
    setFilterPlatform(state, { payload }) {
      state.filteredPlatform = payload;
    },
    setFilterCurrency(state, { payload }) {
      state.filteredCurrency = payload;
    },
    setFilterMethodCode(state, { payload }) {
      state.filteredMethodCode = payload;
    },
    setFilterStartDate(state, { payload }) {
      state.filteredStartDate = payload;
    },
    setFilterEndDate(state, { payload }) {
      state.filteredEndDate = payload;
    },

    removeFilterStatus(state, { payload }) {
      const existingStatus = state.filteredStatus;
      const newFilteredStatus = existingStatus.filter(
        (status) => status !== payload,
      );
      state.filteredStatus = newFilteredStatus;
    },
    removeFilterLoB(state, { payload }) {
      const existingLoB = state.filteredLoB;
      const newFilteredLoB = existingLoB.filter((lob) => lob !== payload);
      state.filteredLoB = newFilteredLoB;
    },
    removeFilterPlatform(state, { payload }) {
      const existingPlatform = state.filteredPlatform;
      const newFilteredPlatform = existingPlatform.filter(
        (platform) => platform !== payload,
      );
      state.filteredPlatform = newFilteredPlatform;
    },
    removeFilterCurrency(state, { payload }) {
      const existingCurrency = state.filteredCurrency;
      const newFilteredCurrency = existingCurrency.filter(
        (currency) => currency !== payload,
      );
      state.filteredCurrency = newFilteredCurrency;
    },
    removeFilterMethodCode(state, { payload }) {
      const existingMethodCode = state.filteredMethodCode;
      const newFilteredMethodCode = existingMethodCode.filter(
        (methodCode) => methodCode !== payload,
      );
      state.filteredMethodCode = newFilteredMethodCode;
    },
    removeFilterStartEndDate(state) {
      state.filteredStartDate = null;
      state.filteredEndDate = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMaintenanceRuleList.pending, (state) => {
        state.maintenanceRuleListStatus = 'loading';
      })
      .addCase(getMaintenanceRuleList.rejected, (state) => {
        state.maintenanceRuleList = [];
        state.maintenanceRuleListStatus = 'failed';
      })
      .addCase(getMaintenanceRuleList.fulfilled, (state, action) => {
        const payload = action.payload;
        const params = action.meta.arg || {};
        const currentPageIndex = params.page || 0; // 0-based page index
        const pageSize = params.size || 30;

        state.maintenanceRuleList = Array.isArray(payload) ? payload : [];
        // Since API never sends total length, we always enable the next page option
        // by setting totalElements to represent at least one more full page.
        state.totalElements = (currentPageIndex + 2) * pageSize;
        state.maintenanceRuleListStatus = 'succeeded';
      });
  },
});

export const paymentOptionsMaintenanceRulesActions =
  paymentOptionsMaintenanceRulesSlice.actions;

export const selectMaintenanceRuleList = (state) =>
  state.paymentOptions.maintenanceRules.maintenanceRuleList;

export const selectIsLoadingMaintenanceRuleList = (state) =>
  state.paymentOptions.maintenanceRules.maintenanceRuleListStatus === 'loading';

export const selectMaintenanceRuleTotalElements = (state) =>
  state.paymentOptions.maintenanceRules.totalElements || 0;

export const selectMaintenanceRuleFilteredStatus = (state) =>
  state.paymentOptions.maintenanceRules.filteredStatus;

export const selectMaintenanceRuleFilteredLoB = (state) =>
  state.paymentOptions.maintenanceRules.filteredLoB;

export const selectMaintenanceRuleFilteredPlatform = (state) =>
  state.paymentOptions.maintenanceRules.filteredPlatform;

export const selectMaintenanceRuleFilteredCurrency = (state) =>
  state.paymentOptions.maintenanceRules.filteredCurrency;

export const selectMaintenanceRuleFilteredMethodCode = (state) =>
  state.paymentOptions.maintenanceRules.filteredMethodCode;

export const selectMaintenanceRuleFilteredStartDate = (state) =>
  state.paymentOptions.maintenanceRules.filteredStartDate;

export const selectMaintenanceRuleFilteredEndDate = (state) =>
  state.paymentOptions.maintenanceRules.filteredEndDate;

export const selectAvailableMaintenanceRuleStatus = (state) => [
  ...new Set(
    state.paymentOptions.maintenanceRules.maintenanceRuleList.map(
      ({ active }) => (active ? 'Enabled' : 'Disabled'),
    ),
  ),
];

export const selectAvailableMaintenanceRuleLoB = (state) => [
  ...new Set(
    state.paymentOptions.maintenanceRules.maintenanceRuleList.map(
      ({ merchant: { lobCode } }) => lobCode,
    ),
  ),
];

export const selectAvailableMaintenanceRulePlatform = (state) => [
  ...new Set(
    state.paymentOptions.maintenanceRules.maintenanceRuleList.map(
      ({
        merchant: {
          merchantDetails: { platformCode },
        },
      }) => platformCode,
    ),
  ),
];

export const selectAvailableMaintenanceRuleCurrency = (state) => [
  ...new Set(
    state.paymentOptions.maintenanceRules.maintenanceRuleList.map(
      ({ currency: { code } }) => code,
    ),
  ),
];

export const selectAvailableMaintenanceRuleMethodCode = (state) => [
  ...new Set(
    state.paymentOptions.maintenanceRules.maintenanceRuleList.map(
      ({ methodCode: { methodCode } }) => methodCode,
    ),
  ),
];

export const selectMaintenanceRuleFilteredList = createSelector(
  selectMaintenanceRuleList,
  selectMaintenanceRuleFilteredStatus,
  selectMaintenanceRuleFilteredLoB,
  selectMaintenanceRuleFilteredPlatform,
  selectMaintenanceRuleFilteredCurrency,
  selectMaintenanceRuleFilteredMethodCode,
  selectMaintenanceRuleFilteredStartDate,
  selectMaintenanceRuleFilteredEndDate,
  (
    maintenanceRules,
    filteredStatus,
    filteredLoB,
    filteredPlatform,
    filteredCurrency,
    filteredMethodCode,
    filteredStartDate,
    filteredEndDate,
  ) => {
    let filteredMaintenanceRules = maintenanceRules;

    if (filteredStatus && filteredStatus.length !== 0) {
      filteredMaintenanceRules = filteredMaintenanceRules.filter(({ active }) =>
        filteredStatus.includes(active ? 'Enabled' : 'Disabled'),
      );
    }

    if (filteredLoB && filteredLoB.length !== 0) {
      filteredMaintenanceRules = filteredMaintenanceRules.filter(
        ({ merchant: { lobCode } }) => filteredLoB.includes(lobCode),
      );
    }

    if (filteredPlatform && filteredPlatform.length !== 0) {
      filteredMaintenanceRules = filteredMaintenanceRules.filter(
        ({
          merchant: {
            merchantDetails: { platformCode },
          },
        }) => filteredPlatform.includes(platformCode),
      );
    }

    if (filteredCurrency && filteredCurrency.length !== 0) {
      filteredMaintenanceRules = filteredMaintenanceRules.filter(
        ({ currency: { code } }) => filteredCurrency.includes(code),
      );
    }

    if (filteredMethodCode && filteredMethodCode.length !== 0) {
      filteredMaintenanceRules = filteredMaintenanceRules.filter(
        ({ methodCode: { methodCode } }) =>
          filteredMethodCode.includes(methodCode),
      );
    }

    if (filteredStartDate || filteredEndDate) {
      filteredMaintenanceRules = filteredMaintenanceRules.filter(
        ({ id, startTime, endTime }) => {
          const isStartDateInFilterRange = dayjs(startTime).isBetween(
            filteredStartDate,
            filteredEndDate,
          );

          const isEndDateInFilterRange = dayjs(endTime).isBetween(
            filteredStartDate,
            filteredEndDate,
          );

          const isFilterStartDateInRuleDateRange = dayjs(
            filteredStartDate,
          ).isBetween(startTime, endTime);

          const isFilterEndDateInRuleDateRange = dayjs(
            filteredEndDate,
          ).isBetween(startTime, endTime);

          return (
            isStartDateInFilterRange ||
            isEndDateInFilterRange ||
            isFilterStartDateInRuleDateRange ||
            isFilterEndDateInRuleDateRange
          );
        },
      );
    }

    return filteredMaintenanceRules;
  },
);

export default paymentOptionsMaintenanceRulesSlice.reducer;
